<template>
  <!-- Картинка — сразу, остальное — строкой: скан диплома надо увидеть, а не
       гадать по имени файла. -->
  <a
    v-if="file.render === 'image'"
    class="msg-att msg-att--image"
    :href="file.url"
    target="_blank"
    rel="noopener"
  >
    <img :src="file.url" :alt="file.original_name" loading="lazy" />
  </a>

  <!-- Голосовое: свой плеер, потому что <audio controls> в пузырь не влезает
       и выглядит чужим на каждом браузере по-своему. -->
  <div v-else-if="file.render === 'voice'" class="msg-att msg-att--voice" :class="{ 'is-mine': mine }">
    <button type="button" class="msg-att__play" :aria-label="playing ? 'Пауза' : 'Слушать'" @click="toggle">
      <svg v-if="!playing" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>
    </button>

    <div class="msg-att__wave" @click="seek">
      <span
        v-for="(bar, i) in bars"
        :key="i"
        class="msg-att__bar"
        :class="{ 'is-played': (i + 0.5) / bars.length * 100 <= progress }"
        :style="{ height: (3 + bar * 17) + 'px' }"
      />
    </div>

    <span class="msg-att__time">{{ clock }}</span>

    <audio
      ref="audio"
      :src="file.url"
      preload="metadata"
      @timeupdate="onTime"
      @loadedmetadata="onMeta"
      @durationchange="onMeta"
      @ended="onEnded"
      @pause="playing = false"
      @play="playing = true"
    />
  </div>

  <!-- Видео — задел: сервер размечает его отдельно, но пока такого не шлёт. -->
  <video v-else-if="file.render === 'video'" class="msg-att msg-att--video" :src="file.url" controls preload="metadata" />

  <!-- Всё прочее — строка с именем и размером. Имя настоящее: на диске лежит
       хеш, и без него человек не узнает собственный диплом. -->
  <a v-else class="msg-att msg-att--file" :class="{ 'is-mine': mine }" :href="file.url" target="_blank" rel="noopener">
    <span class="msg-att__icon" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/>
      </svg>
    </span>
    <span class="msg-att__name">{{ file.original_name }}</span>
    <span class="msg-att__size">{{ humanSize }}</span>
  </a>
</template>

<script>
export default {
  name: 'MessageAttachment',
  props: {
    file: { type: Object, required: true },
    mine: { type: Boolean, default: false },
  },
  data() {
    return {
      playing: false,
      position: 0,
      /**
       * Длительность, вычитанная из самого файла.
       *
       * ОТДЕЛЬНЫМ ПОЛЕМ, а не чтением `$refs` в computed: computed
       * пересчитывается по реактивным зависимостям, а `$refs` к ним не
       * относится. Метаданные приезжают ПОСЛЕ первого счёта — и значение,
       * снятое один раз при `duration_sec: null`, оставалось нулём навсегда:
       * таймер показывал 0:00, полоса не двигалась, перемотка не работала.
       * Голосовое из мобильного приложения (оно длительность не присылает)
       * так и оставалось мёртвым.
       */
      measured: 0,
      // Один разовый «прыжок» ради вычисления длины webm без duration_sec.
      probed: false,
      reset: false,
    };
  },
  computed: {
    /** Длительность знает записавший; если не сказал — вычитаем из файла. */
    duration() {
      return this.file.duration_sec || this.measured || 0;
    },
    /**
     * Столбики волны. Прислали — рисуем их; нет — ровная дорожка-заглушка
     * (не случайная: одна и та же запись не должна выглядеть по-разному при
     * каждой перерисовке).
     */
    bars() {
      const wave = Array.isArray(this.file.waveform) ? this.file.waveform : [];
      if (wave.length) return wave.map((v) => Math.max(0.06, Math.min(1, Number(v) || 0)));
      return Array.from({ length: 40 }, (_, i) => 0.3 + 0.12 * Math.abs(Math.sin(i)));
    },
    progress() {
      return this.duration ? Math.min(100, (this.position / this.duration) * 100) : 0;
    },
    clock() {
      const left = Math.max(0, Math.round((this.duration || 0) - this.position));
      const m = Math.floor(left / 60);
      const s = left % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    humanSize() {
      const units = ['Б', 'КБ', 'МБ'];
      let size = this.file.size || 0;
      let unit = 0;
      while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit += 1;
      }
      return `${String(unit === 0 ? Math.round(size) : size.toFixed(1)).replace('.', ',')} ${units[unit]}`;
    },
  },
  methods: {
    toggle() {
      const el = this.$refs.audio;
      if (!el) return;

      if (this.playing) {
        el.pause();
        this.playing = false;
        return;
      }

      // Одно голосовое за раз: два одновременно — каша, из которой не понять
      // ни одного. Чужие плееры глушим ЧЕРЕЗ ИХ СОБСТВЕННЫЙ обработчик
      // `pause`: `a.pause()` по DOM останавливал звук, но компонент рядом об
      // этом не узнавал и продолжал показывать «идёт воспроизведение».
      document.querySelectorAll('audio').forEach((a) => a !== el && a.pause());
      el.play().catch(() => { this.playing = false; });
    },
    onTime() {
      const el = this.$refs.audio;

      // После «прыжка в конец» браузер отдал длину и уехал в конец записи —
      // тихо возвращаем начало, пока человек не начал слушать.
      if (this.probed && !this.reset && Number.isFinite(el?.duration) && el.duration > 0) {
        this.reset = true;
        this.measured = el.duration;
        el.currentTime = 0;
      }

      this.position = el?.currentTime || 0;
    },
    /** Метаданные доехали — забираем длительность, если её не прислали. */
    onMeta() {
      const el = this.$refs.audio;
      const value = el?.duration;

      if (Number.isFinite(value) && value > 0) {
        this.measured = value;
      } else if (el && value === Infinity && !this.file.duration_sec && !this.probed) {
        // `Infinity` — обычный ответ браузера на webm-запись MediaRecorder:
        // длина неизвестна, пока файл не проигран до конца. Заставляем браузер
        // её вычислить — прыжком в заведомо запредельную позицию; он упрётся в
        // конец, узнает длину и вернёт её через `durationchange`. Один раз.
        this.probed = true;
        try {
          el.currentTime = 1e101;
        } catch { /* перемотка недоступна — обойдёмся без длительности */ }
      }

      this.onTime();
    },
    onEnded() {
      this.playing = false;
      this.position = 0;
    },
    seek(event) {
      const el = this.$refs.audio;
      if (!el || !this.duration) return;

      const box = event.currentTarget.getBoundingClientRect();
      el.currentTime = ((event.clientX - box.left) / box.width) * this.duration;
      this.onTime();
    },
  },
};
</script>

<style lang="scss" scoped>
.msg-att {
  display: block;
  margin-top: 6px;

  &--image {
    max-width: 260px;
    border-radius: 12px;
    overflow: hidden;
    line-height: 0;

    // `contain`, а не `cover`: портретный скан паспорта при 320px по высоте
    // обрезался ровно посередине — человек видел кусок страницы и не понимал,
    // тот ли файл ушёл.
    img {
      width: 100%;
      height: auto;
      max-height: 320px;
      object-fit: contain;
      display: block;
    }
  }

  &--video {
    max-width: 280px;
    border-radius: 12px;
  }

  &--file,
  &--voice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 10px;
    background: rgba(17, 65, 93, 0.06);
    text-decoration: none;
    color: inherit;

    &.is-mine {
      background: rgba(255, 255, 255, 0.18);
    }
  }

  &--file:hover {
    background: rgba(17, 65, 93, 0.1);
  }

  &__icon {
    flex: none;
    opacity: 0.75;
    display: flex;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__size {
    flex: none;
    font-size: 11.5px;
    opacity: 0.7;
  }

  &--voice {
    min-width: 210px;
  }

  &__play {
    flex: none;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: #11415d;
    color: #fff;

    .is-mine & {
      background: rgba(255, 255, 255, 0.9);
      color: #11415d;
    }
  }

  &__wave {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 2px;
    height: 24px;
    cursor: pointer;
    // Подложки НЕТ: в WhatsApp/Telegram видны только столбики, а сплошная
    // светлая полоса под ними читалась «подсвеченной строкой».
  }

  &__bar {
    flex: 1;
    min-width: 2px;
    border-radius: 2px;
    background: rgba(17, 65, 93, 0.25);
    transition: background 0.1s linear;

    &.is-played {
      background: #11415d;
    }

    // В своём (синем) пузыре дорожка светлая: непройденное — приглушённо
    // белым, пройденное — в полную силу, чтобы прогресс был виден и на синем.
    .is-mine & {
      background: rgba(255, 255, 255, 0.45);

      &.is-played {
        background: #fff;
      }
    }
  }

  &__time {
    flex: none;
    font-size: 11.5px;
    font-variant-numeric: tabular-nums;
    opacity: 0.75;
  }
}
</style>
